/**
 * Read-time normalization.
 *
 * Strips Word conditional comments (`<!--[if ...]>...<!--[endif]-->`), which
 * are the only genuine noise in a `getHtml()` selection response. Everything
 * else — class attributes, inline styles including `mso-*` — is preserved.
 *
 * `getHtml()` returns a selection fragment wrapped in html block
 * (`<html>/<head>/<body>`). The content is unwrapped to `body.innerHTML`;
 * the discarded `<head>` includes a `<style>` block describing the SOURCE
 * document's styles, which is intentionally dropped because it enlarges LLM context.
 * Body content — class attributes and inline styles, including `mso-*`
 * is preserved verbatim so no formatting signal is lost upstream.
 *
 * Styles block could potentially be preserved and re-injected at write time,
 * but that would only be required for specific prompts and is planned to be
 * replaced with OOXML-based style scraping and re-emission.
 *
 * Vocabulary enforcement and inline-override blocklists are WRITE-time
 * concerns (LLM response validation), not read-time filters.
 */

import { removeWordConditionalComments } from './remove-word-artifacts'

/**
 * Normalize raw Word `getHtml()` output: strips conditional comments and
 * unwraps full-document responses to bare body HTML. Fragments are returned
 * as-is (no DOM parse).
 */
export function normalizeToCanonical(html: string): string {
  const withoutComments = removeWordConditionalComments(html)

  const isFullDocument = /<html[\s>]|<body[\s>]|<head[\s>]|<!doctype/i.test(withoutComments)
  if (isFullDocument) {
    const doc = new DOMParser().parseFromString(withoutComments, 'text/html')
    return doc.body.innerHTML.trim()
  }

  return withoutComments
}
